/**
 * Settings modal hook for handling scroll-to functionality and auto-save
 *
 * Handles scrolling to specific sections within the settings modal
 * when opened from different contexts (e.g., from engine selector).
 * Also handles auto-saving API keys on blur.
 */
export default {
  mounted() {
    // Find the parent dialog element
    this.dialog = this.el.closest('dialog');

    // Store bound handlers for proper cleanup
    this.boundOpenAIBlur = this.handleOpenAIBlur.bind(this);
    this.boundAnthropicBlur = this.handleAnthropicBlur.bind(this);
    this.boundFormKeydown = this.handleFormKeydown.bind(this);

    // Track current input elements for cleanup
    this.currentOpenAIInput = null;
    this.currentAnthropicInput = null;
    this.currentForm = null;

    // Listen for the scroll-to-api-keys event
    this.handleEvent('scroll-to-api-keys', () => {
      // Wait for dialog to be fully open before scrolling
      if (this.dialog && !this.dialog.open) {
        // If dialog not open yet, wait for it
        this.dialog.addEventListener('transitionend', () => {
          this.scrollToApiKeys();
        }, { once: true });
      } else {
        this.scrollToApiKeys();
      }
    });

    // Setup blur handlers for API key inputs
    this.setupApiKeyBlurHandlers();

    // Setup Enter key handling for skill editor form
    this.setupSkillEditorKeyHandling();
  },

  scrollToApiKeys() {
    const apiKeysSection = document.getElementById('api-keys-section');
    if (apiKeysSection) {
      apiKeysSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  },

  updated() {
    // Re-setup handlers in case inputs were re-rendered
    this.setupApiKeyBlurHandlers();
    this.setupSkillEditorKeyHandling();
  },

  handleOpenAIBlur(e) {
    const value = e.target.value.trim();
    this.pushEvent('save_single_api_key', {
      provider: 'openai',
      value: value
    });
  },

  handleAnthropicBlur(e) {
    const value = e.target.value.trim();
    this.pushEvent('save_single_api_key', {
      provider: 'anthropic',
      value: value
    });
  },

  handleFormKeydown(e) {
    // Only intercept Enter key
    if (e.key !== 'Enter') return;

    // Allow Enter in textareas (for multi-line input)
    if (e.target.tagName === 'TEXTAREA') return;

    // For single-line inputs, prevent form submission
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') {
      e.preventDefault();

      // Move to next focusable element
      const form = e.currentTarget;
      const focusableElements = Array.from(form.querySelectorAll(
        'input:not([type="hidden"]), select, textarea, button[type="submit"]'
      ));
      const currentIndex = focusableElements.indexOf(e.target);
      if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
        focusableElements[currentIndex + 1].focus();
      }
    }
  },

  setupApiKeyBlurHandlers() {
    const openaiInput = document.getElementById('openai-api-key');
    const anthropicInput = document.getElementById('anthropic-api-key');

    // Clean up old handler if input changed
    if (this.currentOpenAIInput && this.currentOpenAIInput !== openaiInput) {
      this.currentOpenAIInput.removeEventListener('blur', this.boundOpenAIBlur);
    }

    // Set up new handler if input exists and is different
    if (openaiInput && openaiInput !== this.currentOpenAIInput) {
      openaiInput.addEventListener('blur', this.boundOpenAIBlur);
      this.currentOpenAIInput = openaiInput;
    }

    // Clean up old handler if input changed
    if (this.currentAnthropicInput && this.currentAnthropicInput !== anthropicInput) {
      this.currentAnthropicInput.removeEventListener('blur', this.boundAnthropicBlur);
    }

    // Set up new handler if input exists and is different
    if (anthropicInput && anthropicInput !== this.currentAnthropicInput) {
      anthropicInput.addEventListener('blur', this.boundAnthropicBlur);
      this.currentAnthropicInput = anthropicInput;
    }
  },

  setupSkillEditorKeyHandling() {
    const form = document.getElementById('skill-editor-form');

    // Clean up old handler if form changed
    if (this.currentForm && this.currentForm !== form) {
      this.currentForm.removeEventListener('keydown', this.boundFormKeydown);
    }

    // Set up new handler if form exists and is different
    if (form && form !== this.currentForm) {
      form.addEventListener('keydown', this.boundFormKeydown);
      this.currentForm = form;
    }
  },

  destroyed() {
    // Clean up all event listeners
    if (this.currentOpenAIInput) {
      this.currentOpenAIInput.removeEventListener('blur', this.boundOpenAIBlur);
    }
    if (this.currentAnthropicInput) {
      this.currentAnthropicInput.removeEventListener('blur', this.boundAnthropicBlur);
    }
    if (this.currentForm) {
      this.currentForm.removeEventListener('keydown', this.boundFormKeydown);
    }
  }
};
